/**
 * 基于 Web Audio API 的音乐管理器
 * 使用 MediaElementSourceNode 连接 HTML5 Audio 元素
 * 结合了 HTML5 Audio 的易用性和 Web Audio API 的强大功能
 */
import { AudioManager } from './audio-manager';
import type { AudioResource, PlayOptions, AudioConfigMap } from './types';
import { getPublicUrl } from '@/utils/cdn';
import { getAudioContextClass } from './audio-utils';

export class MusicManagerWebAudio {
  private audioManager: AudioManager;
  private audioContext: AudioContext | null = null;
  private resources: Map<string, AudioResource> = new Map();
  private currentMusic: HTMLAudioElement | null = null;
  private currentMusicId: string | null = null;
  private currentSourceNode: MediaElementAudioSourceNode | null = null;
  private currentGainNode: GainNode | null = null;
  private fadeInterval: number | null = null;

  constructor(audioManager: AudioManager) {
    this.audioManager = audioManager;
  }

  /**
   * 初始化 AudioContext
   */
  private initAudioContext(): AudioContext {
    if (!this.audioContext) {
      const AudioContextClass = getAudioContextClass();

      if (!AudioContextClass) {
        throw new Error('AudioContext is not supported in this browser');
      }

      this.audioContext = new AudioContextClass();
    }

    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch((error) => {
        console.warn('Failed to resume AudioContext:', error);
      });
    }

    return this.audioContext;
  }

  /**
   * 注册音频资源
   */
  register(id: string, path: string): void {
    this.resources.set(id, {
      id,
      path: getPublicUrl(path),
      loaded: false,
    });
  }

  /**
   * 批量注册音频资源
   */
  registerAll(configMap: AudioConfigMap): void {
    Object.entries(configMap).forEach(([id, path]) => {
      this.register(id, path);
    });
  }

  /**
   * 检查资源是否已注册
   */
  isRegistered(id: string): boolean {
    return this.resources.has(id);
  }

  /**
   * 取消注册/卸载音频资源
   */
  unregister(id: string): void {
    const resource = this.resources.get(id);
    if (resource?.audioElement) {
      resource.audioElement.pause();
      resource.audioElement.src = '';
      resource.audioElement.load();
      resource.audioElement = undefined;
    }
    this.resources.delete(id);
  }

  /**
   * 预加载音频
   */
  async preload(id: string): Promise<void> {
    const resource = this.resources.get(id);
    if (!resource) {
      throw new Error(`Music resource "${id}" is not registered`);
    }

    if (resource.loaded && resource.audioElement) {
      return;
    }

    return new Promise((resolve, reject) => {
      const audio = new Audio(resource.path);
      audio.preload = 'auto';

      audio.addEventListener('canplaythrough', () => {
        resource.audioElement = audio;
        resource.loaded = true;
        resolve();
      });

      audio.addEventListener('error', (e) => {
        reject(
          new Error(
            `Failed to load music "${id}": ${(e.target as HTMLAudioElement).error?.message || 'Unknown error'}`
          )
        );
      });

      audio.load();
    });
  }

  /**
   * 获取或创建音频元素
   */
  private getOrCreateAudioElement(id: string): HTMLAudioElement {
    const resource = this.resources.get(id);
    if (!resource) {
      throw new Error(`Music resource "${id}" is not registered`);
    }

    if (resource.audioElement) {
      return resource.audioElement;
    }

    const audio = new Audio(resource.path);
    audio.preload = 'auto';
    resource.audioElement = audio;
    return audio;
  }

  /**
   * 清理当前的 Web Audio 节点
   */
  private cleanupAudioNodes(): void {
    if (this.currentSourceNode) {
      try {
        this.currentSourceNode.disconnect();
      // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (e) {
        // 忽略断开连接错误
      }
      this.currentSourceNode = null;
    }

    if (this.currentGainNode) {
      try {
        this.currentGainNode.disconnect();
      // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (e) {
        // 忽略断开连接错误
      }
      this.currentGainNode = null;
    }
  }

  /**
   * 播放音乐（循环）
   */
  async playMusic(id: string, options: PlayOptions = {}): Promise<void> {
    if (!this.audioManager.getMusicEnabled()) {
      return;
    }

    if (this.currentMusicId === id && this.currentMusic && !this.currentMusic.paused) {
      return;
    }

    this.stopMusic();

    try {
      const audio = this.getOrCreateAudioElement(id);
      const audioContext = this.initAudioContext();

      // 创建 MediaElementSourceNode（连接 HTML5 Audio）
      const sourceNode = audioContext.createMediaElementSource(audio);

      // 创建增益节点（音量控制）
      const gainNode = audioContext.createGain();
      const volume = this.audioManager.calculateMusicVolume(1.0, options.volume ?? 1.0);
      gainNode.gain.value = volume;

      // 连接节点：audio → sourceNode → gainNode → destination
      sourceNode.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // 设置循环
      audio.loop = options.loop ?? true;

      // 添加事件监听
      if (options.onEnded) {
        audio.addEventListener('ended', options.onEnded, { once: true });
      }

      if (options.onError) {
        audio.addEventListener(
          'error',
          () => {
            options.onError?.(new Error(`Failed to play music "${id}"`));
          },
          { once: true }
        );
      }

      // 播放
      await audio.play();

      // 保存当前状态
      this.currentMusic = audio;
      this.currentMusicId = id;
      this.currentSourceNode = sourceNode;
      this.currentGainNode = gainNode;
    } catch (error) {
      console.error(`Failed to play music "${id}":`, error);
      throw error;
    }
  }

  /**
   * 停止音乐
   */
  stopMusic(): void {
    this.clearFadeInterval();

    if (this.currentMusic) {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
    }

    this.cleanupAudioNodes();
    this.currentMusic = null;
    this.currentMusicId = null;
  }

  /**
   * 暂停音乐
   */
  pauseMusic(): void {
    if (this.currentMusic && !this.currentMusic.paused) {
      this.currentMusic.pause();
    }
  }

  /**
   * 恢复音乐
   */
  resumeMusic(): void {
    if (this.currentMusic && this.currentMusic.paused) {
      this.currentMusic.play().catch((error) => {
        console.error('Failed to resume music:', error);
      });
    }
  }

  /**
   * 清除渐变定时器
   */
  private clearFadeInterval(): void {
    if (this.fadeInterval !== null) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
  }

  /**
   * 渐入播放音乐
   */
  async fadeInMusic(id: string, duration: number = 1000): Promise<void> {
    if (!this.audioManager.getMusicEnabled()) {
      return;
    }

    this.stopMusic();

    try {
      const audio = this.getOrCreateAudioElement(id);
      const audioContext = this.initAudioContext();

      // 创建节点
      const sourceNode = audioContext.createMediaElementSource(audio);
      const gainNode = audioContext.createGain();

      // 初始音量为 0
      gainNode.gain.value = 0;

      // 连接节点
      sourceNode.connect(gainNode);
      gainNode.connect(audioContext.destination);

      audio.loop = true;

      // 播放
      await audio.play();

      // 保存状态
      this.currentMusic = audio;
      this.currentMusicId = id;
      this.currentSourceNode = sourceNode;
      this.currentGainNode = gainNode;

      // 使用 Web Audio API 的渐变
      const targetVolume = this.audioManager.calculateMusicVolume(1.0, 1.0);
      const currentTime = audioContext.currentTime;

      // 线性渐变到目标音量
      gainNode.gain.linearRampToValueAtTime(targetVolume, currentTime + duration / 1000);
    } catch (error) {
      console.error(`Failed to fade in music "${id}":`, error);
      throw error;
    }
  }

  /**
   * 渐出停止音乐
   */
  fadeOutMusic(duration: number = 1000, callback?: () => void): void {
    if (!this.currentMusic || !this.currentGainNode || !this.audioContext) {
      callback?.();
      return;
    }

    const currentTime = this.audioContext.currentTime;

    // 使用 Web Audio API 的渐变
    this.currentGainNode.gain.linearRampToValueAtTime(0, currentTime + duration / 1000);

    // 渐变结束后停止音乐
    setTimeout(() => {
      this.stopMusic();
      callback?.();
    }, duration);
  }

  /**
   * 更新音量（响应全局音量变化）
   */
  updateVolume(): void {
    if (this.currentMusic && this.currentGainNode) {
      const volume = this.audioManager.calculateMusicVolume(1.0, 1.0);
      this.currentGainNode.gain.value = volume;

      // 如果音乐被关闭，暂停播放
      if (!this.audioManager.getMusicEnabled() && !this.currentMusic.paused) {
        this.currentMusic.pause();
      }
    }
  }

  /**
   * 解锁音频上下文
   */
  async unlockAudioContext(): Promise<boolean> {
    try {
      const audioContext = this.initAudioContext();

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // 播放一个静音缓冲区
      const buffer = audioContext.createBuffer(1, 1, 22050);
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start(0);

      return true;
    } catch (error) {
      console.error('Failed to unlock audio context:', error);
      return false;
    }
  }

  /**
   * 清理资源
   */
  destroy(): void {
    this.stopMusic();

    if (this.audioContext) {
      this.audioContext.close().catch((error) => {
        console.warn('Failed to close AudioContext:', error);
      });
      this.audioContext = null;
    }

    this.resources.clear();
  }
}
